import React from 'react';
import {
    ApprovalInstance,
    BusinessActionApprovalInstance,
} from '../../../api/ApprovalApi';
import {
    BusinessActionAttributeChange,
    isEmployeeRelatedAction,
} from '../../../api/BusinessActionApi';
import {DetailSection} from './BaseApprovalDetailComponents';
import Typography from '../../../components/Typography';
import {BaseApprovalDetailModal} from "./BaseApprovalDetailModal";


function formatValue(value: unknown): string {
    if (value == null) return '–';
    if (typeof value === 'boolean') return value ? 'Ja' : 'Nein';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
}

const STATUS_STYLES: Record<string, string> = {
    APPLIED:  'bg-green-50  text-green-700',
    PENDING:  'bg-yellow-50 text-yellow-700',
    REJECTED: 'bg-red-50    text-red-700',
    SKIPPED:  'bg-gray-50   text-gray-500',
};

function AttributeRow({
                          label,
                          value,
                          dimmed = false,
                          status,
                      }: {
    label: string;
    value: string;
    dimmed?: boolean;
    status?: string;
}) {
    return (
        <div className="flex justify-between items-start py-2 border-b border-gray-100 last:border-b-0 gap-3">
            <div className="flex items-center gap-2 min-w-0">
                {status && (
                    <span className={`shrink-0 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium ${STATUS_STYLES[status] ?? STATUS_STYLES.PENDING}`}>
                        {status}
                    </span>
                )}
                <span className="text-sm text-gray-500 truncate">{label}</span>
            </div>
            <span className={`text-sm text-right shrink-0 ${dimmed ? 'text-gray-400 line-through' : 'text-gray-900 font-medium'}`}>
                {value}
            </span>
        </div>
    );
}

export interface BusinessActionApprovalDetailModalProps {
    instance: BusinessActionApprovalInstance;
    open: boolean;
    onClose: () => void;
    onSuccess: (updated: ApprovalInstance) => void;
}

export function BusinessActionApprovalDetailModal({
                                                      instance,
                                                      open,
                                                      onClose,
                                                      onSuccess,
                                                  }: BusinessActionApprovalDetailModalProps) {
    const {approvableEntity} = instance;
    const changes: BusinessActionAttributeChange[] = approvableEntity.attributeChanges ?? [];

    const employee = isEmployeeRelatedAction(approvableEntity)
        ? approvableEntity.employee
        : null;

    const definitionLabel =
        approvableEntity.businessActionDefinition.label ?? approvableEntity.businessActionDefinition.name;

    const requestedAt = new Date(approvableEntity.requestedAt).toLocaleDateString('de-DE', {
        day: 'numeric', month: 'long', year: 'numeric',
    });

    // ── Left: current (previous) values ─────────────────────────────────────

    const leftContent = (
        <>
            <DetailSection title="Aktion">
                <div className="py-2 border-b border-gray-100">
                    <Typography variant="caption" color="secondary">Bezeichnung</Typography>
                    <Typography variant="body2" className="font-medium">{definitionLabel}</Typography>
                </div>
                <div className="py-2">
                    <Typography variant="caption" color="secondary">Beantragt am</Typography>
                    <Typography variant="body2">{requestedAt}</Typography>
                </div>
            </DetailSection>

            <DetailSection title="Aktuelle Werte">
                {changes.length === 0 ? (
                    <p className="py-3 text-sm text-gray-400 text-center">Keine Attribute.</p>
                ) : (
                    changes.map(c => (
                        <AttributeRow
                            key={c.id}
                            label={c.attributeKey}
                            value={formatValue(c.previousValue)}
                            dimmed
                        />
                    ))
                )}
            </DetailSection>
        </>
    );

    const rightContent = (
        <DetailSection title="Beantragte Änderungen">
            {changes.length === 0 ? (
                <p className="py-3 text-sm text-gray-400 text-center">Keine Änderungen.</p>
            ) : (
                changes.map(c => (
                    <AttributeRow
                        key={c.id}
                        label={c.attributeKey}
                        value={formatValue(c.newValue)}
                        status={c.status}
                    />
                ))
            )}
        </DetailSection>
    );

    return (
        <BaseApprovalDetailModal
            instance={instance}
            open={open}
            onClose={onClose}
            onSuccess={onSuccess}
            title="Geschäftsvorgang"
            employee={employee}
        >

        </BaseApprovalDetailModal>
    );
}